import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Card, CardContent, IconButton, LinearProgress, List, ListItem, TextField, Typography } from "@mui/material";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { axiosInstance } from "../../../services/axios";


const SearchButton = () => {
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const handleToggleSearchBar = () => {
    setShowSearchBar(!showSearchBar);
    setQuery("");
    setResults([]);
    setLoading(false);
  };

  const fetchResults = async (queryText: any) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/search?text=${encodeURIComponent(queryText)}`);
      setResults(response.data.searchResults || []);
    } catch (error) {
      console.error("Error fetching search results:", error);
    } finally {
      setLoading(false);
    }
  };


  const debouncedSearch = useCallback(
    debounce((queryText) => {
      if (queryText.trim() !== "") {
        fetchResults(queryText);
      } else {
        setResults([]);
        setLoading(false);
      }
    }, 300),
    []
  );

  const handleSearch = (event: any) => {
    const queryText = event?.target?.value;
    setQuery(queryText);
    debouncedSearch(queryText);
  };

  // Cleanup the debounced function on component unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <>
      <IconButton
        color="primary"
        onClick={handleToggleSearchBar}
        sx={{
          color: "primary.main",
          "&:hover": { bgcolor: "primary.light", color: "white" },
        }}
      >
        <SearchIcon />
      </IconButton>
      {showSearchBar && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0, 0, 0, 0.5)", // Semi-transparent background
            zIndex: 1299,
          }}
          onClick={handleToggleSearchBar}
        >
          <Box
            onClick={(e) => e.stopPropagation()} // Prevent click on backdrop from closing the popup
            sx={{
              position: "fixed",
              top: "10%",
              left: "50%",
              transform: "translate(-50%, -0%)",
              width: "60%",
              bgcolor: "background.paper",
              boxShadow: 3,
              borderRadius: 2,
              zIndex: 1300,
              p: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <TextField
                fullWidth
                placeholder="Search..."
                value={query}
                onChange={handleSearch}
                autoFocus
                InputProps={{
                  style: {
                    fontSize: 18,
                  },
                }}
              />
              <IconButton onClick={handleToggleSearchBar}>
                <CloseIcon />
              </IconButton>
            </Box>
            {loading && <LinearProgress sx={{ mb: 1 }} />}
            {results.length > 0 ? (
              <Box sx={{ maxHeight: "60vh", overflow: "auto" }}>
                {results.map((section, index) => (
                  <Box key={index} sx={{ mb: 3, textAlign: "left" }}>
                    <Typography variant="overline" sx={{ fontWeight: 600 }}>
                      {section.name}
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        height: "1px",
                        bgcolor: "grey.300",
                        my: 1,
                      }}
                    />
                    <List >
                      {section.data.map((item: any) => (
                        <ListItem key={item.id} disableGutters sx={{p:0}}>
                          <Card className="hand" sx={{ width: "100%" }} onClick={() => window.open(`/projects/${item.projectId}?assetid=${item?.id}`, "_self")}>
                            <CardContent sx={{p:0, m:0}}>
                              <Typography variant="overline" color="textSecondary" sx={{ fontSize: "0.4em" }}>{item.groupName} / {item.projectName}</Typography>
                              <Typography variant="h6">
                                {item.name}
                              </Typography>
                            </CardContent>
                          </Card>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </Box>
            ) : (
              query && !loading && <Typography sx={{ mt: 2 }}>No results found.</Typography>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default SearchButton;
